import { FieldValues, UseFormReset } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { logger } from '@/utils/logger'

interface QueryParamsHook<T extends FieldValues> {
    getParam: (key: keyof T) => string | null
    getAllParams: () => Partial<T>
    setParam: (key: keyof T, value: string) => void
    removeParam: (key: keyof T) => void
    setFormValues: (reset: UseFormReset<T>) => void
}

export function useQueryParams<T extends FieldValues>(): QueryParamsHook<T> {
    const [searchParams] = useSearchParams()

    logger.debug(
        'Raw searchParams:',
        Object.fromEntries(searchParams.entries())
    )

    const convertValue = (value: string, key: keyof T): T[keyof T] => {
        // Add type conversion logic here
        if (value === 'true') {
            return true as T[keyof T]
        }
        if (value === 'false') {
            return false as T[keyof T]
        }
        if (value === 'null') {
            return null as T[keyof T]
        }
        if (value === 'undefined') {
            return undefined as T[keyof T]
        }
        if (!isNaN(Number(value))) {
            return Number(value) as T[keyof T]
        }

        return value as T[keyof T]
    }

    const getAllParams = (): Partial<T> => {
        const params = {} as Partial<T>

        searchParams.forEach((value, key) => {
            params[key as keyof T] = convertValue(value, key as keyof T)
            logger.debug(`Setting param ${key}:`, value)
        })

        logger.debug('Final params object:', params)

        return params
    }

    const setFormValues = (reset: UseFormReset<T>): void => {
        const params = getAllParams()

        logger.debug('Setting form values with:', params)
        reset(params as T)
    }

    const getParam = (key: keyof T): string | null => {
        return searchParams.get(String(key))
    }

    const setParam = (key: keyof T, value: string): void => {
        searchParams.set(String(key), value)
    }

    const removeParam = (key: keyof T): void => {
        searchParams.delete(String(key))
    }

    return { getParam, getAllParams, setParam, removeParam, setFormValues }
}
